import { useState, useEffect, useRef, useCallback } from "react";
import { Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import _, { round } from "lodash";

import Head from "@/components/Content/Head";
import Fetching from "@/components/Fetching/Fetching";
import CustomProcessingModal from "@/components/desktop/modal/CustomProcessingModal";

import { saveAppOrder } from "@/api/ApplicationOrders/applicationOrders";

import { useAppOrderApprove, useAppOrderCreate, useAppOrderReject, useAppOrderSave } from "@/hooks/planning/application-order/applicationOrders";
import { useDownloadPdf } from "@/hooks/planning/application-order/appOrdersDocs";

import { getToday } from "@/services/Utils";
import { baseAppOrder, baseItem } from "./createOrderApplication.constants";
import { calculateItemDoses } from "./create-order-application.helpers";
import { baseFilters } from "../List/orderApplication.constants";

import CreateAppOrderHead from "./CreateOrderApplicationHead";
import CreateAppOrderButton from "./CreateOrderApplicationButton";
import CreateAppOrderTableDetail from "./CreateOrderApplicationTableDetail";
import CreateAppOrderSignatures from "./CreateOrderApplicationSignatures";
import CreateAppOrderMachineries from "./CreateOrderApplicationMachineries";
import CreateAppOrderDocument from "./CreateOrderApplicationDocument";

import classes from "./createOrderApplication.module.scss";

const CreateAppOrder = () => {
	const { option, code } = useParams();
	const { estate_parent_id, estate_id, search, parameter } = useSelector((state) => state.auth.filter);

	const base = {
		...baseFilters,
		estate_parent_id,
		estate_id,
		search,
		parameter,
	};

	const [data, setData] = useState({ ...baseAppOrder });

	const savedRef = useRef(false);
	const dataRef = useRef(data);
	const baseRef = useRef(base);
	dataRef.current = data;
	baseRef.current = base;

	const params = { estate_parent_id, estate_id, code, get_generics: 1, get_stock: 1 };

	const { data: response, isLoading, isFetching } = useAppOrderCreate(params);

	const { mutate: save, isLoading: isSaving } = useAppOrderSave(base);
	const { mutate: downloadPDF, isLoading: isDownloadingPDF } = useDownloadPdf(code);
	const { mutate: approve, isLoading: loadApprove } = useAppOrderApprove(base);
	const { mutate: reject, isLoading: loadReject } = useAppOrderReject(base);

	const persistDraftIfNeeded = useCallback(() => {
		if (savedRef.current) return;
		if (option === "ver" || option === "editar") return;

		const currentData = dataRef.current;
		if (currentData.application_order_type_id && currentData.code && currentData.estate_parent_id && currentData.estate_id) {
			saveAppOrder({ params: { ...currentData, draft: true }, base: baseRef.current }).catch(() => {});
		}
	}, [option]);

	useEffect(() => {
		return persistDraftIfNeeded;
	}, [persistDraftIfNeeded]);

	useEffect(() => {
		if (!response) return;

		let newData;

		const {
			business = [],
			estates_parents = [],
			estates = [],
			business_centers = [],
			phenological_states = [],
			machineries = [],
			operators = [],
		} = response || {};

		if (response.app_order && ["ver", "editar"].includes(option)) {
			newData = { ...response.app_order };
			newData.all_product = Number(newData.all_product) === 1 ? "all" : "customer";
		} else {
			newData = { ...baseAppOrder };
			newData.code = response.code || "";
			newData.application_order_date = getToday();

			if (business && business.length === 1) {
				newData.business_id = business[0].value;
			}

			if (estates_parents && estates_parents.length === 1) {
				newData.estate_parent_id = estates_parents[0].value;
			}

			if (estates && estates.length === 1) {
				newData.estate_id = estates[0].value;
			}

			if (business_centers && business_centers.length === 1) {
				newData.business_centers = [
					{
						value: business_centers[0].value,
						label: business_centers[0].label,
						specie: business_centers[0].specie,
						variety: business_centers[0].variety,
						area: business_centers[0].area ? String(business_centers[0].area).replace(".", ",") : 0,
						area_select: business_centers[0].area ? String(business_centers[0].area).replace(".", ",") : 0,
					},
				];
			}

			if (phenological_states && phenological_states.length === 1) {
				newData.application_orders_phenological_state_id = phenological_states[0].value;
			}

			if (!newData.machineries || newData.machineries.length === 0) {
				newData.machineries = [{}];
			}

			if (!newData.items || newData.items.length === 0) {
				newData.items = [{ ...baseItem }];
			}

			if (machineries && machineries.filter((m) => m.machinery_type_id === 1).length === 1) {
				newData.machineries[0] = {
					...newData.machineries[0],
					machinery_id: machineries.filter((m) => m.machinery_type_id === 1)[0].value,
					machinery_name: machineries.filter((m) => m.machinery_type_id === 1)[0].label,
				};
			}
			if (machineries && machineries.filter((m) => m.machinery_type_id === 2).length === 1) {
				newData.machineries[0] = {
					...newData.machineries[0],
					tractor_id: machineries.filter((m) => m.machinery_type_id === 2)[0].value,
					tractor_name: machineries.filter((m) => m.machinery_type_id === 2)[0].label,
				};
			}
			if (operators && operators.length === 1) {
				newData.machineries[0] = {
					...newData.machineries[0],
					operator_id: operators[0].value,
					operator_name: operators[0].label,
				};
			}

			if (newData.machineries.length >= 1 && !Object.prototype.hasOwnProperty.call(newData.machineries[0], "calibration")) {
				newData.machineries[0].calibration = "";
			}
			if (newData.machineries.length >= 1 && !Object.prototype.hasOwnProperty.call(newData.machineries[0], "wetness")) {
				newData.machineries[0].wetness = "";
			}
			if (newData.machineries.length >= 1 && !Object.prototype.hasOwnProperty.call(newData.machineries[0], "machined")) {
				newData.machineries[0].machined = "";
			}
		}

		if (response.signatures && Array.isArray(response.signatures)) {
			const sigList = response.signatures.map((sig) => ({
				profile_id: sig.id,
				name: sig.name,
				user_id:
					response.app_order && ["ver", "editar"].includes(option) && response.app_order.signatures?.length > 0
						? response.app_order.signatures.find((s) => Number(s.profile_id) === Number(sig.id))?.user_id || ""
						: sig.users && sig.users.length === 1
							? sig.users[0].value
							: "",
				users: sig.users || [],
			}));
			newData.signatures = sigList;
		}

		setData(newData);
	}, [response, option]);

	const calculateTotal = (params) => {
		let newData = { ...params.newData };

		const totalArea = newData.business_centers.reduce((sum, item) => {
			const parsed = Number(String(item.area_select).replace(",", "."));
			return sum + (isNaN(parsed) ? 0 : parsed);
		}, 0);

		const wetness = newData.machineries.length !== 0 ? newData.machineries[0].wetness : 0;
		const machined = newData.machineries.length !== 0 ? newData.machineries[0].machined : 0;

		if (!newData.machinery_integer) {
			newData.items = newData.items.filter((item) => Boolean(item.isCopy) === false);
		}

		let final_machined = machined;
		let show_division = false;

		const context = { wetness, machined, totalArea, appTypeId: data.application_order_type_id };

		const newItems = newData.items
			.filter((item) => Boolean(item.isCopy) === false)
			.map((item) => {
				let clonedItem = calculateItemDoses(item, context, params.type);

				if (Array.isArray(clonedItem.product_options) && clonedItem.product_options.length > 0) {
					clonedItem = {
						...clonedItem,
						product_options: clonedItem.product_options.map((opt) => calculateItemDoses(opt, context, params.type)),
					};
				}

				if (clonedItem.quantity_machinery % 1 === 0) {
					newData.machinery_integer = false;
				}

				if (clonedItem.quantity_machinery <= 1 && newData.machinery_integer) {
					final_machined = totalArea * wetness;
					clonedItem = { ...clonedItem, quantity_machinery: 1 };
				}

				return { ...clonedItem, machined: final_machined };
			})
			.flatMap((item, index, array) => {
				const hasClone = array.some((el) => el.isCopy);
				const hasOneMachinery = item.quantity_machinery <= 1;
				const hasDecimal = item.quantity_machinery % 1 !== 0;
				const hasCalculated = newData.machinery_integer;

				if (hasClone || !hasDecimal || !hasCalculated || hasOneMachinery) {
					return [item];
				}

				show_division = true;

				const quantityUsed = item.quantity;
				let integerPart = Math.floor(item.quantity_machinery);
				const decimalPart = item.quantity_machinery - integerPart;
				let percentIntegerPart = integerPart / item.quantity_machinery;
				const percentDecimalPart = decimalPart / item.quantity_machinery;

				const modifiedCopy = {
					...item,
					isCopy: true,
					quantity_machinery: 1,
					machined: decimalPart * machined,
					dose_machinery: round((item.dose_100L * (decimalPart * machined)) / 100 / 1000, 2),
					quantity: round(quantityUsed * percentDecimalPart, 2),
				};
				delete modifiedCopy.id;
				delete modifiedCopy.detail_id;

				if (integerPart === 0) {
					integerPart = 1;
					percentIntegerPart = integerPart / item.quantity_machinery;
				}

				item.quantity_machinery = integerPart;
				item.machined = machined;
				item.quantity = round(quantityUsed * percentIntegerPart, 2);

				return [item, modifiedCopy];
			});

		newData.machineries.map((machinery) => {
			machinery.machined = final_machined;
			return machinery;
		});

		setData((prevData) => ({ ...prevData, ...newData, items: newItems, show_division }));
	};

	return (
		<>
			<Head
				title={option === "ingresar" ? "Ingresar Orden de Aplicación" : `${_.capitalize(option)} Orden de Aplicación`}
				isFetching={isFetching && !isLoading}
			/>

			<CustomProcessingModal open={isSaving || isDownloadingPDF || loadApprove || loadReject} message="Procesando orden de aplicación..." />

			{isLoading ? (
				<Fetching />
			) : (
				<Row className={classes.row_global_card}>
					<CreateAppOrderHead params={params} data={data} setData={setData} calculateTotal={calculateTotal} />
					{![2, 5].includes(Number(data.application_order_type_id)) && (
						<CreateAppOrderMachineries params={params} data={data} setData={setData} calculateTotal={calculateTotal} />
					)}
					<CreateAppOrderTableDetail params={params} data={data} setData={setData} calculateTotal={calculateTotal} />
					<CreateAppOrderSignatures params={params} data={data} setData={setData} />
					<CreateAppOrderDocument data={data} setData={setData} />
					<CreateAppOrderButton
						params={params}
						base={base}
						data={data}
						setData={setData}
						save={save}
						approve={approve}
						reject={reject}
						downloadPDF={downloadPDF}
						option={option}
						savedRef={savedRef}
						isSaving={isSaving}
						loadApprove={loadApprove}
						loadReject={loadReject}
						isDownloadingPDF={isDownloadingPDF}
					/>
				</Row>
			)}
		</>
	);
};

export default CreateAppOrder;
